// @figma file=tbd node=tbd name="ChatConversation" set="code-factory" verified=tbd

import { ArrowDown } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../../lib/utils';
import { Button } from '../../atoms/button';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ChatConversationProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Whether to auto-scroll when new children appear */
  autoScroll?: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────

const ChatConversation = React.forwardRef<HTMLDivElement, ChatConversationProps>(
  ({ autoScroll = true, children, className, ...props }, ref) => {
    const innerRef = React.useRef<HTMLDivElement>(null);
    const [showScrollBtn, setShowScrollBtn] = React.useState(false);

    // Combine refs
    React.useImperativeHandle(ref, () => innerRef.current as HTMLDivElement);

    const scrollToBottom = React.useCallback((behavior: ScrollBehavior = 'smooth') => {
      innerRef.current?.scrollTo({ top: innerRef.current.scrollHeight, behavior });
    }, []);

    // Auto-scroll when children change
    React.useLayoutEffect(() => {
      if (!autoScroll) return;
      const el = innerRef.current;
      if (!el) return;
      const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
      if (isNearBottom) scrollToBottom('instant');
    });

    // Show scroll-to-bottom button when not at bottom
    const handleScroll = () => {
      const el = innerRef.current;
      if (!el) return;
      setShowScrollBtn(el.scrollHeight - el.scrollTop - el.clientHeight > 120);
    };

    return (
      <div className="relative flex-1 min-h-0">
        <div
          ref={innerRef}
          onScroll={handleScroll}
          className={cn(
            'h-full overflow-y-auto flex flex-col justify-end gap-4 px-4 py-4',
            className,
          )}
          {...props}
        >
          {children}
        </div>

        {showScrollBtn && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2">
            <Button
              size="icon"
              appearance="outline"
              className="size-7 rounded-full shadow-md"
              onClick={() => scrollToBottom()}
              aria-label="Scroll to bottom"
            >
              <ArrowDown className="size-3.5" />
            </Button>
          </div>
        )}
      </div>
    );
  },
);
ChatConversation.displayName = 'ChatConversation';

export { ChatConversation };
